import { useQuery } from '@tanstack/react-query'
import { useShallow } from 'zustand/react/shallow'
import { GLTF, GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'

import { arcus, KEYS } from '@/lib/arcus/arcus'
import { GLTFObject } from '@/lib/cad/GLTFObject'
import { useCADPageStore } from '@/state/cad'
import { QUERY_KEYS } from '../queryKeys'
import { useCanvasContext } from '@/lib/cad/CanvasProvider'
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js'

const loader = new GLTFLoader()
loader.setMeshoptDecoder(MeshoptDecoder)

const checkServiceWorkerCache = async (downloadUrl: string) => {
  const cleanDownloadUrl = downloadUrl.split('?')[0]
  try {
    if (typeof caches === 'undefined') return false
    const res = await caches.match(cleanDownloadUrl)
    const cached = Boolean(res?.ok)
    return cached
  } catch (err) {
    console.error('Cache lookup failed:', err)
    return false
  }
}

export const getGLTF = async ({
  downloadUrl,
  onLoadingProgress,
}: {
  downloadUrl: string
  onLoadingProgress: (progress: number) => void
}): Promise<{ gltf: GLTFObject }> =>
  new Promise((resolve, reject) => {
    let totalBytes = 0

    loader.load(
      downloadUrl,
      (gltf: GLTF) => {
        onLoadingProgress(100)
        resolve({
          gltf: new GLTFObject(
            gltf.scene,
            totalBytes,
            gltf.userData?.optimized,
          ),
        })
      },
      (xhr) => {
        if (xhr.total > 0) totalBytes = xhr.total
      },
      (err) => reject(err),
    )
  })

export const useGLTF = ({
  downloadUrl,
  documentId,
  cadVersionId,
}: {
  downloadUrl?: string | null
  documentId?: string | null
  cadVersionId?: string | null
}) => {
  const isEnabled = !!downloadUrl && !!cadVersionId
  const setLoadingProgress = useCADPageStore(
    useShallow((state) => state.setLoadingProgress),
  )

  const { gltf, canvasID } = useCanvasContext()
  const shouldUseCache = Boolean(gltf && cadVersionId === canvasID)

  return useQuery({
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    staleTime: 0,
    gcTime: 0,
    enabled: isEnabled,
    queryKey: [QUERY_KEYS.GLTF, documentId, cadVersionId],
    queryFn: async (): Promise<{ gltf?: GLTFObject }> => {
      if (shouldUseCache && cadVersionId) return { gltf }
      if (downloadUrl && cadVersionId) {
        const isCached = await checkServiceWorkerCache(downloadUrl)
        arcus.start(KEYS.MODEL_LOADED, { isCached })
        return getGLTF({
          downloadUrl,
          onLoadingProgress: (progress) => setLoadingProgress(progress),
        })
      }
      return {}
    },
  })
}
